import React, { useState, useRef, useEffect } from 'react';
import { Typography, Box, Button, useMediaQuery, useTheme, Grid } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { EditMode } from './types';
import { StepButtonBar } from './StepButtonBar';
import OtherRequirementInfo from './OtherRequirementInfo';

const PRIMARY = '#c4122f';

interface Step1ChoiceProps {
  onModeSelect: (mode: EditMode) => void;
  onCancel: () => void;
  currentEditMode: EditMode;
  currentStepName: string;
  otherRequirementInfo: any;
  setOtherRequirementInfo: (any) => void;
}

const Step1Choice: React.FC<Step1ChoiceProps> = ({
  onModeSelect,
  onCancel,
  currentEditMode,
  currentStepName,
  otherRequirementInfo,
  setOtherRequirementInfo,
}) => {
  const [selectedMode, setSelectedMode] = useState<EditMode>(currentEditMode ?? null);
  const prevModeRef = useRef<EditMode>(currentEditMode ?? null);
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);

  useEffect(() => {
    setSelectedMode(currentEditMode ?? null);
    prevModeRef.current = currentEditMode ?? null;
  }, [currentEditMode]);

  const handleNext = () => {
    if (selectedMode) {
      onModeSelect(selectedMode);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            variant="outlined"
            onClick={onCancel}
            style={{ borderColor: PRIMARY, color: PRIMARY }}
          >
            {formatMessage({ id: 'form.cancel', defaultMessage: 'Cancel' })}
          </Button>
        </Box>
      </Box>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <Box style={{ maxWidth: '320px', width: '100%' }}>
            <Grid container spacing={3} justifyContent="flex-start">
              <Grid
                item
                xs={12}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <Button
                  variant={selectedMode === 'add' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedMode('add')}
                  style={{
                    padding: isMobile ? '18px 24px' : '22px 32px',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: 700,
                    color: selectedMode === 'add' ? '#fff' : PRIMARY,
                    background: selectedMode === 'add' ? PRIMARY : 'transparent',
                    width: '100%',
                    minWidth: '230px',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.2s ease',
                    border: `2px solid ${PRIMARY}`,
                  }}
                >
                  {formatMessage({
                    id: 'law-portal.manage.requirements.add',
                    defaultMessage: 'Add Requirement',
                  })}
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <Button
                  variant={selectedMode === 'edit' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedMode('edit')}
                  style={{
                    padding: isMobile ? '18px 24px' : '22px 32px',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: 700,
                    color: selectedMode === 'edit' ? '#fff' : PRIMARY,
                    background: selectedMode === 'edit' ? PRIMARY : 'transparent',
                    width: '100%',
                    minWidth: '230px',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.2s ease',
                    border: `2px solid ${PRIMARY}`,
                  }}
                >
                  {formatMessage({
                    id: 'law-portal.manage.requirements.edit',
                    defaultMessage: 'Edit Requirement',
                  })}
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <Button
                  variant={selectedMode === 'delete' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedMode('delete')}
                  style={{
                    padding: isMobile ? '18px 24px' : '22px 32px',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: 700,
                    color: selectedMode === 'delete' ? '#fff' : PRIMARY,
                    background: selectedMode === 'delete' ? PRIMARY : 'transparent',
                    width: '100%',
                    minWidth: '230px',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.2s ease',
                    border: `2px solid ${PRIMARY}`,
                  }}
                >
                  {formatMessage({
                    id: 'law-portal.manage.requirements.delete',
                    defaultMessage: 'Delete Requirement',
                  })}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </div>

        <OtherRequirementInfo otherRequirementStepInfo={otherRequirementInfo} currentStepName={currentStepName} onUpdate={setOtherRequirementInfo} />
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={() => {}}
          onNext={handleNext}
          nextLabel="form.next"
          nextDisabled={selectedMode === null}
          formatMessage={formatMessage}
          showBack={false}
        />
      </Box>
    </div>
  );
};

export default Step1Choice;
